// inv/src/components/ui/lastdealsCompact.jsx
import { useState, memo } from "react";
import { Calendar } from "lucide-react";
import DealRecipientCompany from "./dealRecipientCompany";

const AmountPill = memo(function AmountPill({ value }) {
  if (!value) return null;
  return (
    <div
      className="inline-flex h-6 items-center justify-center rounded-full bg-white ring-1 ring-gray-200 text-[10px] font-semibold text-emerald-700 px-2 shadow-sm"
      title={String(value)}
    >
      {value}
    </div>
  );
});

const RoundBadge = memo(function RoundBadge({ value }) {
  if (!value) return null;
  return (
    <div
      className="inline-flex h-6 items-center rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-2 text-[10px] font-semibold shadow whitespace-nowrap"
      title={String(value)}
    >
      {value}
    </div>
  );
});

const IndustryBadge = memo(function IndustryBadge({ value }) {
  if (!value) return null;
  return (
    <div
      className="inline-flex min-h-6 items-center rounded-full bg-stone-100 border border-stone-200 px-2 text-[10px] font-medium text-stone-700 whitespace-normal break-words max-w-full"
      title={String(value)}
    >
      {value}
    </div>
  );
});

export default function TimelineCompact({
  items = [],
  className = "",
  moreText = "Read more",
  lessText = "Read less",
  recipientAccess = "professionalOnly",
}) {
  const [open, setOpen] = useState({});
  const toggle = (i) => setOpen((s) => ({ ...s, [i]: !s[i] }));

  const InvestorsSection = ({ title, values }) => {
    if (!values?.length) return null;
    return (
      <div className="mt-2">
        <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
          {title}
        </div>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {values.map((node, idx) => (
            <span
              key={idx}
              className="inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium border-gray-300 text-gray-700"
            >
              {node}
            </span>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={className}>
      {/* instead of space-y-3 — grid with 1 column on mobile and 2 columns on md+ */}
      <ol className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
        {items.map((item, i) => {
          const detailParagraphs = Array.isArray(item.detailParagraphs)
            ? item.detailParagraphs
            : [];
          const hasDetails = Boolean(item.summary || detailParagraphs.length || item.details);

          return (
            <li key={item.id ?? i} className="h-full">
              <div className="h-full rounded-lg bg-stone-50 p-3 md:p-3.5 border border-stone-200">
                {/* top row: date + amount + round */}
                <div className="flex flex-wrap items-center gap-1.5">
                  {(item.announcementDate || item.date) && (
                    <span className="inline-flex h-6 items-center border border-stone-200 shadow-sm whitespace-nowrap rounded-full bg-stone-900 text-white pl-2 pr-2.5 text-[10px] font-semibold">
                      <Calendar
                        aria-hidden
                        className="mr-1 h-3 w-3 opacity-80"
                      />
                      {item.announcementDate || item.date}
                    </span>
                  )}

                  <AmountPill value={item.amount} />

                  {item.round && <RoundBadge value={item.round} />}
                </div>

                {/* heading */}
                <h3 className="mt-2 text-[13px] md:text-[14px] leading-snug font-semibold text-gray-900 break-words">
                  {item.title}
                </h3>

                {(item.hasRecipientCompanyInfo || item.recipientCompany || item.recipientCompanyUrl) && (
                  <div className="mt-2">
                    <DealRecipientCompany
                      companyName={item.recipientCompany}
                      companyUrl={item.recipientCompanyUrl}
                      hasInfo={item.hasRecipientCompanyInfo}
                      accessMode={recipientAccess}
                      className="text-[12px] md:text-[13px]"
                    />
                  </div>
                )}

                {/* industries */}
                {Array.isArray(item.industries) &&
                  item.industries.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {item.industries.map((ind, idx) => (
                        <IndustryBadge key={idx} value={ind} />
                      ))}
                    </div>
                  )}

                {/* investors */}
                <InvestorsSection
                  title="Lead investors"
                  values={item.leadInvestors}
                />
                <InvestorsSection
                  title="Investors participated"
                  values={item.otherInvestors}
                />

                {/* details */}
                {hasDetails && (
                  <>
                    <button
                      type="button"
                      onClick={() => toggle(i)}
                      className="mt-2 h-7 cursor-pointer inline-flex items-center justify-center rounded-md border px-2.5 text-[11px] font-medium border-gray-300 text-gray-700 hover:bg-gray-50"
                      aria-expanded={!!open[i]}
                      aria-controls={`details-compact-${i}`}
                    >
                      {open[i] ? lessText : moreText}
                    </button>

                    <div
                      id={`details-compact-${i}`}
                      className={`${
                        open[i] ? "block" : "hidden"
                      } mt-2 text-[12px] leading-relaxed text-gray-700`}
                    >
                      {item.summary && (
                        <p className="mb-1.5">{item.summary}</p>
                      )}
                      {detailParagraphs.length > 0
                        ? detailParagraphs.map((paragraph, paragraphIndex) => (
                            <p
                              key={`${item.id ?? i}-compact-${paragraphIndex}`}
                              className="mb-1.5 leading-relaxed"
                              dangerouslySetInnerHTML={{ __html: paragraph }}
                            />
                          ))
                        : item.details}
                    </div>
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
